import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Hero from '../components/Hero';
import PropertyGallery from '../components/PropertyGallery';
import ExposeSections from '../components/ExposeSections';
import InteractiveMap from '../components/InteractiveMap';
import TransactionTimeline from '../components/TransactionTimeline';
import ContactForm from '../components/ContactForm';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Download, Building2, Mail, ArrowUp, Printer } from 'lucide-react';
import { propertyAPI } from '../services/api';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const RestaurantPage = () => {
  const { t, i18n } = useTranslation();
  const [restaurantData, setRestaurantData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const loadRestaurantData = async () => {
      try {
        setLoading(true);
        const currentLang = i18n.language || 'de';
        
        // Load all restaurant data
        const [hero, quickFacts, highlights, details, transactionProcess, exposeData] = await Promise.all([
          fetch(`${API_BASE_URL}/api/property/restaurant/hero?lang=${currentLang}`).then(r => r.json()),
          fetch(`${API_BASE_URL}/api/property/restaurant/quick-facts?lang=${currentLang}`).then(r => r.json()),
          fetch(`${API_BASE_URL}/api/property/restaurant/highlights?lang=${currentLang}`).then(r => r.json()),
          propertyAPI.getDetails(currentLang),
          propertyAPI.getTransactionProcess(currentLang),
          fetch(`${API_BASE_URL}/api/property/restaurant/expose-data?lang=${currentLang}`).then(r => r.json())
        ]);
        
        setRestaurantData({
          hero: hero,
          quickFacts: quickFacts,
          investmentHighlights: highlights.highlights,
          restaurant: details.restaurant,
          transactionProcess: transactionProcess.steps,
          exposeSections: exposeData.sections,
          contact: {
            email: "d.boettche@webvision-nord.de"
          }
        });
      } catch (error) {
        console.error('Error loading restaurant data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadRestaurantData();
  }, [i18n.language]);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 500);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        <Header />
        <div className="container mx-auto px-4 py-20 text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[#C9A961] mx-auto mb-4"></div>
          <p className="text-xl text-slate-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (!restaurantData) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        <Header />
        <div className="container mx-auto px-4 py-20 text-center">
          <p className="text-xl text-slate-600">{t('common.loadingError')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Header />
      
      {/* Hero Section with Restaurant Hero Data */}
      <Hero data={restaurantData.hero} />

      {/* Quick Facts - Restaurant specific */}
      <section className="py-20 bg-white border-b border-slate-200" id="overview">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
            <Card className="text-center border-2 border-[#C9A961]/20 hover:border-[#C9A961] transition-all hover:shadow-xl">
              <CardContent className="pt-6 pb-6">
                <p className="text-sm text-slate-600 mb-2 font-semibold">{t('restaurant.quickFacts.rooms')}</p>
                <p className="text-2xl font-bold text-[#0A2540]">{restaurantData.quickFacts.rooms}</p>
              </CardContent>
            </Card>
            <Card className="text-center border-2 border-[#C9A961]/20 hover:border-[#C9A961] transition-all hover:shadow-xl">
              <CardContent className="pt-6 pb-6">
                <p className="text-sm text-slate-600 mb-2 font-semibold">{t('restaurant.quickFacts.events')}</p>
                <p className="text-2xl font-bold text-[#0A2540]">{restaurantData.quickFacts.events}</p>
              </CardContent>
            </Card>
            <Card className="text-center border-2 border-[#C9A961]/20 hover:border-[#C9A961] transition-all hover:shadow-xl">
              <CardContent className="pt-6 pb-6">
                <p className="text-sm text-slate-600 mb-2 font-semibold">{t('restaurant.quickFacts.staff')}</p>
                <p className="text-2xl font-bold text-[#0A2540]">{restaurantData.quickFacts.staff}</p>
              </CardContent>
            </Card>
            <Card className="text-center border-2 border-[#C9A961]/20 hover:border-[#C9A961] transition-all hover:shadow-xl">
              <CardContent className="pt-6 pb-6">
                <p className="text-sm text-slate-600 mb-2 font-semibold">{t('restaurant.quickFacts.parking')}</p>
                <p className="text-2xl font-bold text-[#0A2540]">{restaurantData.quickFacts.parking}</p>
              </CardContent>
            </Card>
            <Card className="text-center border-2 border-[#C9A961]/20 hover:border-[#C9A961] transition-all hover:shadow-xl">
              <CardContent className="pt-6 pb-6">
                <p className="text-sm text-slate-600 mb-2 font-semibold">{t('restaurant.quickFacts.special')}</p>
                <p className="text-2xl font-bold text-[#0A2540]">{restaurantData.quickFacts.special}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Investment Highlights - Restaurant specific */}
      <section className="py-24 bg-gradient-to-br from-slate-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <Badge className="mb-6 bg-gradient-to-r from-[#C9A961] to-[#D4AF37] text-white px-6 py-2 text-base font-semibold shadow-lg">{t('highlights.badge')}</Badge>
            <h2 className="text-5xl md:text-6xl font-extrabold text-[#0A2540] mb-6 leading-tight">{t('highlights.title')}</h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
              {t('highlights.subtitle')}
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-[#C9A961] to-[#D4AF37] mx-auto rounded-full mt-6"></div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {restaurantData.investmentHighlights.map((highlight, index) => (
              <Card key={index} className="border-2 border-[#C9A961]/20 hover:border-[#C9A961] transition-all hover:shadow-2xl hover:-translate-y-1">
                <CardContent className="p-8">
                  <div className="w-14 h-14 bg-gradient-to-br from-[#C9A961] to-[#D4AF37] rounded-full flex items-center justify-center mb-6 shadow-lg">
                    <span className="text-2xl">🏆</span>
                  </div>
                  <h3 className="text-2xl font-bold text-[#0A2540] mb-4">{highlight.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{highlight.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Property Gallery - Restaurant */}
      <section className="py-24 bg-white" id="properties">
        <div className="container mx-auto px-4">
          <div className="text-center mb-20">
            <Badge className="mb-6 bg-gradient-to-r from-[#2C5F66] to-[#0A2540] text-white px-6 py-2 text-base font-semibold shadow-lg">{t('restaurant.galleryBadge')}</Badge>
            <h2 className="text-5xl md:text-6xl font-extrabold text-[#0A2540] mb-6 leading-tight">{t('restaurant.galleryTitle')}</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-[#C9A961] to-[#D4AF37] mx-auto rounded-full"></div>
          </div>
          <PropertyGallery 
            title={restaurantData.restaurant.title}
            description={restaurantData.restaurant.description}
            features={restaurantData.restaurant.features}
            images={restaurantData.restaurant.images}
          />
        </div>
      </section>

      {/* Detailed Exposé Sections - Restaurant */}
      {restaurantData.exposeSections && (
        <section className="py-24 bg-gradient-to-br from-slate-50 to-white" id="expose">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <Badge className="mb-6 bg-gradient-to-r from-[#C9A961] to-[#D4AF37] text-white px-6 py-2 text-base font-semibold shadow-lg">{t('expose.badge')}</Badge>
              <h2 className="text-5xl md:text-6xl font-extrabold text-[#0A2540] mb-6 leading-tight">{t('expose.title')}</h2>
              <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
                {t('expose.subtitle')}
              </p>
              <div className="w-24 h-1 bg-gradient-to-r from-[#C9A961] to-[#D4AF37] mx-auto rounded-full mt-6"></div>
            </div>
            <ExposeSections data={{ sections: restaurantData.exposeSections }} />
          </div>
        </section>
      )}

      {/* PDF Download Section */}
      <section className="py-16 bg-gradient-to-r from-[#0A2540] to-[#2C5F66] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-6">{t('restaurant.downloadTitle')}</h2>
            <p className="text-lg text-slate-200 mb-8">{t('restaurant.downloadSubtitle')}</p>
            <div className="flex flex-wrap justify-center gap-4">
              <a 
                href={`${API_BASE_URL}/api/property/restaurant-expose-pdf`}
                className="inline-flex items-center px-8 py-4 bg-[#C9A961] hover:bg-[#D4AF37] text-white font-semibold rounded-lg transition-all shadow-lg hover:shadow-xl"
              >
                <Download className="w-5 h-5 mr-2" />
                {t('restaurant.downloadDE')}
              </a>
              <a 
                href={`${API_BASE_URL}/api/property/restaurant-expose-pdf-en`}
                className="inline-flex items-center px-8 py-4 bg-[#C9A961] hover:bg-[#D4AF37] text-white font-semibold rounded-lg transition-all shadow-lg hover:shadow-xl"
              >
                <Download className="w-5 h-5 mr-2" />
                {t('restaurant.downloadEN')}
              </a>
              <a 
                href={`${API_BASE_URL}/api/property/restaurant-expose-pdf-nl`}
                className="inline-flex items-center px-8 py-4 bg-[#C9A961] hover:bg-[#D4AF37] text-white font-semibold rounded-lg transition-all shadow-lg hover:shadow-xl"
              >
                <Download className="w-5 h-5 mr-2" />
                {t('restaurant.downloadNL')}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Map - Same as main page */}
      <section className="py-24 bg-gradient-to-br from-slate-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <Badge className="mb-6 bg-gradient-to-r from-[#C9A961] to-[#D4AF37] text-white px-6 py-2 text-base font-semibold shadow-lg">{t('sections.locationBadge')}</Badge>
            <h2 className="text-5xl md:text-6xl font-extrabold text-[#0A2540] mb-6 leading-tight">{t('sections.locationTitle')}</h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
              {t('sections.locationSubtitle')}
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-[#C9A961] to-[#D4AF37] mx-auto rounded-full mt-6"></div>
          </div>
          <InteractiveMap />
        </div>
      </section>

      {/* Transaction Timeline - Same as main page */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <Badge className="mb-6 bg-gradient-to-r from-[#2C5F66] to-[#0A2540] text-white px-6 py-2 text-base font-semibold shadow-lg">{t('sections.timelineBadge')}</Badge>
            <h2 className="text-5xl md:text-6xl font-extrabold text-[#0A2540] mb-6 leading-tight">{t('sections.timelineTitle')}</h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
              {t('sections.timelineSubtitle')}
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-[#C9A961] to-[#D4AF37] mx-auto rounded-full mt-6"></div>
          </div>
          <TransactionTimeline data={restaurantData.transactionProcess} />
        </div>
      </section>

      {/* Contact Section - Same as main page */}
      <section className="py-24 bg-gradient-to-br from-[#0A2540] via-[#1E3A5F] to-[#2C5F66] text-white relative overflow-hidden" id="contact">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(201,169,97,0.1),transparent_50%)]"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(212,175,55,0.1),transparent_50%)]"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-extrabold mb-6 leading-tight">{t('sections.contactTitle')}</h2>
            <p className="text-xl text-slate-200 max-w-3xl mx-auto leading-relaxed">
              {t('sections.contactSubtitle')}
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-[#C9A961] to-[#D4AF37] mx-auto rounded-full mt-6"></div>
          </div>
          <ContactForm />
        </div>
      </section>

      {/* Footer - Same as main page */}
      <footer className="bg-[#0A2540] text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <Building2 className="w-6 h-6" />
                <span className="font-bold text-lg">{t('restaurant.title')}</span>
              </div>
              <p className="text-slate-300 text-sm">
                {t('footer.description')}
              </p>
            </div>
            <div>
              <h3 className="font-bold mb-4">{t('footer.contactTitle')}</h3>
              <div className="space-y-2 text-sm text-slate-300">
                <div className="flex items-center space-x-2">
                  <Mail className="w-4 h-4" />
                  <span>d.boettche@webvision-nord.de</span>
                </div>
              </div>
            </div>
            <div>
              <h3 className="font-bold mb-4">{t('footer.legalTitle')}</h3>
              <div className="space-y-2 text-sm text-slate-300">
                <p>{t('footer.copyright')}</p>
                <p>
                  <a href="/impressum" className="hover:text-[#C9A961] transition-colors">
                    Impressum
                  </a>
                  {' '} | {' '}
                  <a href="/datenschutz" className="hover:text-[#C9A961] transition-colors">
                    Datenschutz
                  </a>
                </p>
                <p>{t('footer.disclaimer')}</p>
                <p>{t('footer.bonityRequired')}</p>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 w-12 h-12 bg-[#C9A961] text-white rounded-full shadow-lg hover:bg-[#D4AF37] transition-all duration-300 flex items-center justify-center z-50 hover:scale-110 print:hidden"
          aria-label="Scroll to top"
        >
          <ArrowUp className="w-6 h-6" />
        </button>
      )}

      {/* Print Button */}
      <button
        onClick={handlePrint}
        className="fixed bottom-8 right-24 w-12 h-12 bg-[#0A2540] text-white rounded-full shadow-lg hover:bg-[#0d3052] transition-all duration-300 flex items-center justify-center z-50 hover:scale-110 print:hidden"
        title={t('common.printPage')}
      >
        <Printer className="w-6 h-6" />
      </button>
    </div>
  );
};

export default RestaurantPage;
